/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef } from "react";
import { devLog } from "@/utils/devLog";

/**
 * Gère l'attachement d'un stream (HLS ou MP3) à l'élément audio.
 * HLS.js est chargé en lazy-import (économie ~30KB sur le bundle initial).
 * Renvoie attachStream(radio) et un cleanup hlsRef.
 */
export function useStreamAttacher(audioRef, onPlayStart, onError) {
  const hlsRef = useRef(null);

  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  const destroyHls = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, []);

  const attachMp3 = useCallback(
    (audio, streamUrl) => {
      const sep = streamUrl.includes("?") ? "&" : "?";
      audio.src = `${streamUrl}${sep}_t=${Date.now()}`;
    },
    []
  );

  const attachHls = useCallback(
    async (audio, streamUrl) => {
      try {
        const { default: Hls } = await import("hls.js");
        if (!Hls.isSupported()) {
          audio.src = streamUrl;
          onPlayStart();
          return;
        }
        const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
        hlsRef.current = hls;
        hls.loadSource(streamUrl);
        hls.attachMedia(audio);
        hls.on(Hls.Events.MANIFEST_PARSED, onPlayStart);
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (data.fatal) {
            devLog("HLS fatal error", data);
            onError();
          }
        });
      } catch (err) {
        devLog("HLS dynamic import failed", err);
        onError();
      }
    },
    [onPlayStart, onError]
  );

  const attachStream = useCallback(
    (radio) => {
      const audio = audioRef.current;
      if (!audio) return;
      const isHls = radio.stream_type === "hls";
      const canNativeHls = audio.canPlayType("application/vnd.apple.mpegurl");
      if (isHls && !canNativeHls) {
        // Chargement asynchrone de hls.js puis attachement
        attachHls(audio, radio.stream_url);
        return;
      }
      if (isHls && canNativeHls) {
        audio.src = radio.stream_url;
      } else {
        attachMp3(audio, radio.stream_url);
      }
      onPlayStart();
    },
    [attachHls, attachMp3, onPlayStart]
  );

  return { attachStream, destroyHls };
}
